import type { Currency } from '../../../constants';
import { type WoocommercePaymentMethodValue } from '../../../constants/woocommerce/woocommerce';
import type {
  BillingAddress,
  FeeLine,
  ShippingAddress,
  ShippingLine,
  WooCommerceLineItem,
  WooCommerceOrderResponse,
} from '../../../model/woocommerce/woocommerce';

export interface CreateWooCommerceOrderRequest {
  actorId?: string;
  cartToken: string;
  paymentMethod: WoocommercePaymentMethodValue;
  paymentMethodTitle: string;
  customerId?: number;
  status?: string;
  setPaid?: boolean;
  total: number;
  currency: Currency;
  shippingTotal: number;
  discountTotal: number;
  billing: BillingAddress;
  shipping: ShippingAddress;
  lineItems: WooCommerceLineItem[];
  shippingLines?: ShippingLine[];
  feeLines: FeeLine[];
  couponLines?: Array<{
    code: string;
    discount: string;
    autoApplied: boolean;
  }>;
  paymentProcessInfo: {
    stripePaymentIntentId?: string;
  };
  utm?: {
    sourceType?: string; //type
    utmCampaign?: string; // cmp
    utmMedium?: string; //mdm
    utmSource?: string; //src
    utmId?: string;
  };
  adAttribution?: {
    adStorage: 'granted' | 'denied';
    fbc?: string;
    fbclid?: string;
    gclid?: string;
    ttclid?: string;
  };
  loyaltyCredit?: LoyaltyCreditPayload;
}

/**
 * Loyalty credit payload for order creation.
 *
 * Server rules (400 otherwise):
 * - Send exactly one of: { redemptionPoint } OR { creditAmount + currency } — never both
 * - All amounts must be > 0
 * - `currency` must equal the order's top-level `currency`
 * - `discountAmount` is server-authoritative; any client value is overwritten
 */
export type LoyaltyCreditPayload =
  | {
      /** Legacy path — redemption points only. */
      redemptionPoint: number;
      creditAmount?: never;
      currency?: never;
    }
  | {
      /** New path — credit amount in display currency (must match order currency). */
      creditAmount: number;
      currency: Currency;
      redemptionPoint?: never;
    };

export type CreateWooCommerceOrderResponse = WooCommerceOrderResponse;
